import type { APIRoutes } from 'api';
import { environment } from 'environment';
import type { MakeNeverKeysOptional, NotEmpty } from 'types';
import { NetEssentials, type ApiConfig, type HTTPMethod } from './essentials';

const makeHeaders = () => ({ 'Content-Type': 'application/json', 'x-visitor-id': NetEssentials.visitorId });
const parseContentType = (contentType: string): 'json' | 'text' | 'blob' | 'formData' | null => {
  if (contentType.includes('application/json')) return 'json';
  if (contentType.includes('text/plain')) return 'text';
  if (contentType.includes('text/html')) return 'text';
  if (contentType.includes('multipart/form-data')) return 'formData';
  if (contentType.includes('application/octet-stream')) return 'blob';
  return null;
};

type APISuccess<
  CK extends keyof APIRoutes,
  RK extends keyof APIRoutes[CK],
  DangerousTypeOverride extends object | null = null,
> = {
  success: true;
  data: DangerousTypeOverride extends null ? GetPath<APIRoutes, [CK, RK, 'returns']> : DangerousTypeOverride;
};
type APIError<CK extends keyof APIRoutes, RK extends keyof APIRoutes[CK]> = {
  success: false;
  error:
    | GetPath<APIRoutes, [CK, RK, 'errors']>
    | { path: string; status: -99; statusText: 'already fetching'; message: 'already fetching'; timestamp: string };
  softFail?: boolean;
};

type APIResult<
  CK extends keyof APIRoutes,
  RK extends keyof APIRoutes[CK],
  DangerousTypeOverride extends object | null = null,
> = APISuccess<CK, RK, DangerousTypeOverride> | APIError<CK, RK>;

type FetchResponseType = 'json' | 'text' | 'blob' | 'formData';

/**
 * fucking hell TS team holy shit
 * https://github.com/microsoft/TypeScript/issues/21760
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Tail<T extends any[]> = T extends [any, ...infer R] ? R : never;
type GetProp<T, K> = K extends keyof T ? T[K] : never;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GetPath<T, P extends any[]> = P extends [any] ? GetProp<T, P[0]> : GetPath<GetProp<T, P[0]>, Tail<P>>;

type OptArgs<CK extends keyof APIRoutes, RK extends keyof APIRoutes[CK]> = MakeNeverKeysOptional<{
  body: GetPath<APIRoutes, [CK, RK, 'body']>;
  query: GetPath<APIRoutes, [CK, RK, 'query']>;
  params: GetPath<APIRoutes, [CK, RK, 'params']>;
}>;
type Args<CK extends keyof APIRoutes, RK extends keyof APIRoutes[CK]> =
  NotEmpty<OptArgs<CK, RK>> extends never ? undefined : OptArgs<CK, RK>;

type APIMethodArgs<CK extends keyof APIRoutes, RK extends keyof APIRoutes[CK], Method extends HTTPMethod> =
  Args<CK, RK> extends undefined
    ? [
        path: GetPath<APIRoutes, [CK, RK, 'method']> extends Method
          ? GetPath<APIRoutes, [CK, RK, 'path']> extends infer P
            ? P extends string
              ? P
              : never
            : never
          : never,
        args?: Args<CK, RK>,
        extraConfig?: RequestInit,
        resType?: FetchResponseType,
      ]
    : [
        path: GetPath<APIRoutes, [CK, RK, 'method']> extends Method
          ? GetPath<APIRoutes, [CK, RK, 'path']> extends infer P
            ? P extends string
              ? P
              : never
            : never
          : never,
        args: Args<CK, RK>,
        extraConfig?: RequestInit,
        resType?: FetchResponseType,
      ];

class Api {
  private static readonly base = environment.urls.api;
  private static fetching = new Set<string>();

  static get = <
    CK extends keyof APIRoutes,
    RK extends keyof APIRoutes[CK],
    DangerousTypeOverride extends object | null = null,
  >(
    ...[path, args, extraConfig, resType]: APIMethodArgs<CK, RK, 'GET'>
  ): Promise<APIResult<CK, RK, DangerousTypeOverride>> => this.network(path, args, 'GET', extraConfig, resType);

  static post = <
    CK extends keyof APIRoutes,
    RK extends keyof APIRoutes[CK],
    DangerousTypeOverride extends object | null = null,
  >(
    ...[path, args, extraConfig, resType]: APIMethodArgs<CK, RK, 'POST'>
  ): Promise<APIResult<CK, RK, DangerousTypeOverride>> => this.network(path, args, 'POST', extraConfig, resType);

  static delete = <
    CK extends keyof APIRoutes,
    RK extends keyof APIRoutes[CK],
    DangerousTypeOverride extends object | null = null,
  >(
    ...[path, args, extraConfig, resType]: APIMethodArgs<CK, RK, 'DELETE'>
  ): Promise<APIResult<CK, RK, DangerousTypeOverride>> => this.network(path, args, 'DELETE', extraConfig, resType);

  static patch = <
    CK extends keyof APIRoutes,
    RK extends keyof APIRoutes[CK],
    DangerousTypeOverride extends object | null = null,
  >(
    ...[path, args, extraConfig, resType]: APIMethodArgs<CK, RK, 'PATCH'>
  ): Promise<APIResult<CK, RK, DangerousTypeOverride>> => this.network(path, args, 'PATCH', extraConfig, resType);

  static put = <
    CK extends keyof APIRoutes,
    RK extends keyof APIRoutes[CK],
    DangerousTypeOverride extends object | null = null,
  >(
    ...[path, args, extraConfig, resType]: APIMethodArgs<CK, RK, 'PUT'>
  ): Promise<APIResult<CK, RK, DangerousTypeOverride>> => this.network(path, args, 'PUT', extraConfig, resType);

  static head = <
    CK extends keyof APIRoutes,
    RK extends keyof APIRoutes[CK],
    DangerousTypeOverride extends object | null = null,
  >(
    ...[path, args, extraConfig, resType]: APIMethodArgs<CK, RK, 'HEAD'>
  ): Promise<APIResult<CK, RK, DangerousTypeOverride>> => this.network(path, args, 'HEAD', extraConfig, resType);

  static options = <
    CK extends keyof APIRoutes,
    RK extends keyof APIRoutes[CK],
    DangerousTypeOverride extends object | null = null,
  >(
    ...[path, args, extraConfig, resType]: APIMethodArgs<CK, RK, 'OPTIONS'>
  ): Promise<APIResult<CK, RK, DangerousTypeOverride>> => this.network(path, args, 'OPTIONS', extraConfig, resType);

  private static async handleResponse<CK extends keyof APIRoutes, RK extends keyof APIRoutes[CK]>(
    resType: FetchResponseType,
    res: Response,
  ): Promise<APISuccess<CK, RK>> {
    if (res.status === 204 || res.headers.get('Content-Length') === '0') return { success: true, data: null };
    if (parseContentType(res.headers.get('Content-Type')))
      return { success: true, data: await res[parseContentType(res.headers.get('Content-Type'))]() };

    if (resType) return { success: true, data: await res[resType]() };

    return { success: true, data: null };
  }

  private static handleError = async <CK extends keyof APIRoutes, RK extends keyof APIRoutes[CK]>(
    res: Response,
  ): Promise<APIError<CK, RK>> => {
    if (res.status >= 500)
      await import('state/src/alerts').then(({ useAlerts }) =>
        useAlerts.getState().addAlert({
          title: 'Something went terribly wrong...',
          body: "Hmm... the server really didn't like that. The Beds for Kids team has been notified. Sorry for the inconvenience!",
          icon: 'error',
        }),
      );

    return { success: false, error: await res.json() };
  };

  private static async network<CK extends keyof APIRoutes, RK extends keyof APIRoutes[CK]>(
    path: string,
    args: {
      body?: unknown;
      query?: { [key: string]: string | number | boolean | Date };
      params?: { [key: string]: string | number };
    },
    method: HTTPMethod,
    extraConfig: ApiConfig = {},
    resType: FetchResponseType,
  ): Promise<APIResult<CK, RK>> {
    const [uri, conf] = this.makeOptions(path, args, method, extraConfig);

    if (this.fetching.has(uri.toString())) {
      console.warn(`already fetching ${uri.toString()} - cancelling...`);

      return {
        success: false,
        error: {
          path: uri.toString(),
          status: -99,
          statusText: 'already fetching',
          message: 'already fetching',
          timestamp: new Date().toISOString(),
        },
        softFail: true,
      };
    }
    this.fetching.add(uri.toString());

    const result = await this.doRequest(uri, conf);

    this.fetching.delete(uri.toString());

    if (!result) return;

    const [success, res] = result;

    if (!success) return await this.handleError<CK, RK>(res);
    return await this.handleResponse<CK, RK>(resType, res);
  }

  private static async doRequest(url: URL, config: RequestInit): Promise<[boolean, Response]> {
    await pollForAppLaunch(url);
    const res = await fetch(url, { ...config, credentials: 'include' });

    if (typeof window !== 'undefined' && res.status === 401 && !url.toString().includes('/auth/login'))
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return void (await import('../../../apps/web/scripts/auth').then(({ Auth }) => Auth.logout(true)));

    return [res.ok, res];
  }

  private static makeOptions(
    path: string,
    args: {
      body?: unknown;
      query?: { [key: string]: string | number | boolean | Date };
      params?: { [key: string]: string | number };
    },
    method: HTTPMethod,
    extraConfig: ApiConfig = {},
  ): [URL, ApiConfig] {
    // param urls are in the format /route/:param/:param2
    if (args && 'params' in args) for (const p in args.params) path = path.replace(`:${p}`, String(args.params[p]));

    const uri = new URL(`${this.base}${path}`);

    if (args && 'query' in args)
      for (const p in args.query) {
        if (args.query[p] === undefined || args.query[p] === null) continue;
        let value = args.query[p];

        if (value instanceof Date) value = value.toISOString();
        else value = String(value);

        uri.searchParams.append(p, value);
      }

    const headers = typeof window === 'undefined' ? { visitorId: 'server' } : makeHeaders();
    const config = {
      headers,
      method,
      body: args && 'body' in args ? JSON.stringify(args.body) : undefined,
      ...extraConfig,
    };

    return [uri, config];
  }
}

export { Api };

/** @danger */
export const unwrap = <
  CK extends keyof APIRoutes,
  RK extends keyof APIRoutes[CK],
  DangerousTypeOverride extends object | null,
>(
  res: Promise<APIResult<CK, RK, DangerousTypeOverride>>,
) =>
  res.then(r => {
    if (r?.success) return r.data;
    return undefined;
  });

export const unwrapThrow = async <
  CK extends keyof APIRoutes,
  RK extends keyof APIRoutes[CK],
  DangerousTypeOverride extends object | null,
>(
  res: Promise<APIResult<CK, RK, DangerousTypeOverride>>,
) => {
  const unwrapped = await unwrap(res);
  if (!unwrapped) throw new Error('API call failed');
  return unwrapped;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Unwrapped<P extends Promise<APIResult<any, any>>> =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  P extends Promise<APIResult<infer CK extends keyof APIRoutes, any>>
    ? P extends Promise<APIResult<CK, infer RK extends keyof APIRoutes[CK]>>
      ? GetPath<APIRoutes, [CK, RK, 'returns']>
      : never
    : never;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RouteReturn<P extends (...params: never[]) => Promise<APIResult<any, any>>> = Awaited<
  Unwrapped<ReturnType<P>>
>;

const allowedAppLaunch = ['/flags', '/auth/search-keys', '/user', '/auth/logout', '/auth/logout/clean'];
async function pollForAppLaunch(url: URL): Promise<void> {
  if (allowedAppLaunch.includes(url.pathname)) return;
  if (typeof window === 'undefined' || (!window?.app?.isLaunching && window?.app?.hasLaunched)) return;

  let attempts = 0;
  while (window?.app?.isLaunching || !window?.app?.hasLaunched) {
    await new Promise(r => setTimeout(r, 100));
    attempts++;

    if (attempts >= 100) {
      console.warn('appLaunch took too long, retrying...');
      await import('state/src/alerts').then(({ useAlerts }) =>
        useAlerts.getState().addAlert({
          title: "Hmm... That's Weird.",
          body: 'Your request is taking longer than expected. Retrying...',
          icon: 'warning',
        }),
      );

      await (await import('../../../apps/web/scripts/appLaunch')).appLaunch(true);
      attempts = 0;
    }
  }
}
